import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "./auth/AuthContext";
import { useIsSubscribed } from "../hooks/useIsSubscribed";
import { useSubscription } from "../hooks/useSubscription";
import { useTier } from "../hooks/useTier";
import ManageSubscriptionButton from "./ManageSubscriptionButton";
import Modal from "./common/Modal";
import stripeCheckoutService from "../services/stripeCheckoutService";
import * as tierService from "../services/tierService";
import {
  simulateSubscription,
  cancelSimulatedSubscription,
} from "../services/subscriptionTestService";
// Note: useLocation removed - we use window.location directly to avoid re-render triggers
import { useTranslation } from "../hooks/useTranslation";
import { useLanguage } from "./language/LanguageContext";
import {
  trackContentView,
  trackCTAClick,
  trackFeatureUsed,
} from "../services/analyticsService";
import "./ManageSubscription.css";

/**
 * Manage Subscription Component
 *
 * Shows subscription information if user is subscribed, or
 * subscription plans if user is not subscribed.
 */
const ManageSubscription = () => {
  const { currentUser } = useAuth();
  const t = useTranslation();
  const { language } = useLanguage();
  const { isSubscribed, loading: subscriptionLoading } = useIsSubscribed();
  const {
    tier: userTier,
    tierId: userTierId,
    loading: tierLoading,
  } = useTier();
  const [selectedBillingPeriod, setSelectedBillingPeriod] = useState("yearly");
  const [testLoading, setTestLoading] = useState(false);
  const [testError, setTestError] = useState(null);
  const [testSuccess, setTestSuccess] = useState(null);
  const hasProcessedUrlParams = useRef(false);

  // Get locale based on language (en -> en-US, es -> es-ES)
  const dateLocale = language === "es" ? "es-ES" : "en-US";

  // Helper function to format dates with proper locale
  const formatDate = (date, options = {}) => {
    if (!date) return "";
    if (!(date instanceof Date)) return String(date);

    const defaultOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
      ...options,
    };

    return date.toLocaleDateString(dateLocale, defaultOptions);
  };
  const {
    subscription,
    loading: subscriptionDetailsLoading,
    currentPeriodEnd,
    status,
    cancelAtPeriodEnd,
    isActive,
    isRecurring,
    startDate,
    endDate,
  } = useSubscription();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  // Track page view when component loads
  useEffect(() => {
    if (currentUser) {
      trackContentView({
        section: "billing",
        content_type: "page",
        additionalParams: {
          has_subscription: isSubscribed,
          subscription_status: status || "none",
        },
      });
    }
  }, [currentUser, isSubscribed, status]);

  // Check for success/cancel query params - runs only once on mount
  useEffect(() => {
    // Prevent multiple executions of URL param handling
    if (hasProcessedUrlParams.current) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const hasSuccess = params.get("success") === "true";
    const hasCanceled = params.get("canceled") === "true";

    if (hasSuccess || hasCanceled) {
      // Mark as processed immediately to prevent re-runs
      hasProcessedUrlParams.current = true;

      // Clean up URL immediately to prevent any re-triggers
      window.history.replaceState({}, "", window.location.pathname);

      if (hasSuccess) {
        // Use a stable message - don't rely on t() in state that might re-render
        const message = t(
          "subscription.activatedSuccess",
          "Subscription activated successfully!"
        );
        setSuccessMessage(message);

        // Track successful subscription activation
        trackFeatureUsed({
          section: "billing",
          feature: "subscription_checkout",
          action: "success",
          source: "stripe_redirect",
        });
      }

      if (hasCanceled) {
        const message = t(
          "subscription.checkoutCanceled",
          "Subscription checkout was canceled."
        );
        setError(message);

        // Track checkout cancellation
        trackFeatureUsed({
          section: "billing",
          feature: "subscription_checkout",
          action: "canceled",
          source: "stripe_redirect",
        });
      }
    }
  }, []); // Run only once on mount - we read URL directly from window.location

  // Handle test subscription simulation
  const handleTestSubscription = async (tierId, billingPeriod) => {
    if (!currentUser) {
      setTestError(t("subscription.testMustLogin", "You must be logged in to simulate subscriptions"));
      return;
    }

    setTestLoading(true);
    setTestError(null);
    setTestSuccess(null);

    try {
      await simulateSubscription(tierId, billingPeriod, currentUser.uid);
      setTestSuccess(
        t("subscription.testSimulateSuccess", "Successfully simulated {tierId} {billingPeriod} subscription! Page will refresh...")
          .replace("{tierId}", tierId)
          .replace("{billingPeriod}", billingPeriod)
      );

      // Clear success message and reload after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      console.error("Error simulating subscription:", err);
      setTestError(err.message || t("subscription.testSimulateFailed", "Failed to simulate subscription"));
      setTestLoading(false);
    }
  };

  // Handle cancel test subscription
  const handleCancelTestSubscription = async () => {
    if (!currentUser) {
      setTestError(t("subscription.testCancelMustLogin", "You must be logged in to cancel subscriptions"));
      return;
    }

    setTestLoading(true);
    setTestError(null);
    setTestSuccess(null);

    try {
      const result = await cancelSimulatedSubscription(currentUser.uid);
      setTestSuccess(
        t("subscription.testCancelSuccess", "Successfully canceled {count} test subscription(s)! Page will refresh...")
          .replace("{count}", result.canceledCount)
      );

      // Clear success message and reload after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      console.error("Error canceling subscription:", err);
      setTestError(err.message || t("subscription.testCancelFailed", "Failed to cancel subscription"));
      setTestLoading(false);
    }
  };

  // Handle subscription plan selection
  const handleSubscribe = async (priceId, tierId, billingPeriod) => {
    if (!currentUser) {
      setError(
        t("subscription.mustLogin", "You must be logged in to subscribe.")
      );
      return;
    }

    // Track subscription initiation
    trackCTAClick({
      section: "billing",
      action: "start_subscription",
      source: "button",
      plan_tier: tierId || "free",
      target_plan: billingPeriod,
      additionalParams: {
        tier_id: tierId,
        price_id: priceId,
      },
    });

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await stripeCheckoutService.createSession({
        priceId,
        userId: currentUser.uid,
        successUrl: `${window.location.origin}/manage-subscription?success=true`,
        cancelUrl: `${window.location.origin}/manage-subscription?canceled=true`,
        redirect: true,
      });

      // Track successful checkout session creation
      trackFeatureUsed({
        section: "billing",
        feature: "subscription_checkout",
        action: "redirect_to_stripe",
        source: "button",
        additionalParams: {
          tier_id: tierId,
          billing_period: billingPeriod,
          price_id: priceId,
        },
      });
    } catch (err) {
      console.error("Error creating checkout session:", err);
      setError(
        err.message ||
          t(
            "subscription.subscribeFailed",
            "Failed to start subscription. Please try again."
          )
      );

      // Track checkout error
      trackFeatureUsed({
        section: "billing",
        feature: "subscription_checkout",
        action: "error",
        source: "button",
        additionalParams: {
          tier_id: tierId,
          billing_period: billingPeriod,
          error: err.message,
        },
      });

      setLoading(false);
    }
  };

  // Show login prompt if not authenticated
  if (!currentUser) {
    return (
      <div className="manage-subscription-container">
        <div className="manage-subscription-card">
          <h2>{t("subscription.title", "Manage Subscription")}</h2>
          <p>
            {t(
              "subscription.loginPrompt",
              "Please log in to manage your subscription or view available plans."
            )}
          </p>
        </div>
      </div>
    );
  }

  // Show loading state
  if (subscriptionLoading || subscriptionDetailsLoading || tierLoading) {
    return (
      <div className="manage-subscription-container">
        {/* Success Modal - rendered even during loading to prevent flash */}
        <Modal
          isOpen={!!successMessage}
          onClose={() => setSuccessMessage(null)}
          type="success"
          message={successMessage}
        />

        {/* Error Modal - rendered even during loading */}
        <Modal
          isOpen={!!error}
          onClose={() => setError(null)}
          type="error"
          message={error}
        />

        <div className="manage-subscription-card">
          <h2>{t("subscription.title", "Manage Subscription")}</h2>
          <div className="loading-state">
            <div className="spinner"></div>
            <p>
              {t("subscription.loading", "Loading subscription information...")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show subscription information if user is subscribed
  // Note: userTier will be the free tier object if user has no subscription
  if (isSubscribed && subscription && userTier && userTierId !== "free") {
    // Determine billing period from priceId
    const priceIds = tierService.getPriceIdsForTier(userTierId);
    const isMonthly = subscription.priceId === priceIds?.monthly;
    const isYearly = subscription.priceId === priceIds?.yearly;
    const billingPeriod = isYearly ? "yearly" : isMonthly ? "monthly" : "monthly";

    // Get tier display name
    const tierName = tierService.getTierName(userTier);
    const planName = `${tierName}${
      billingPeriod === "yearly"
        ? t("subscription.planPeriodYearly", " (Yearly)")
        : t("subscription.planPeriodMonthly", " (Monthly)")
    }`;

    return (
      <div className="manage-subscription-container">
        {/* Error Modal */}
        <Modal
          isOpen={!!error}
          onClose={() => setError(null)}
          type="error"
          message={error}
        />

        {/* Success Modal */}
        <Modal
          isOpen={!!successMessage}
          onClose={() => setSuccessMessage(null)}
          type="success"
          message={successMessage}
        />

        <div className="manage-subscription-card">
          <h2>{t("subscription.yourSubscription", "Your Subscription")}</h2>

          <div className="subscription-info">
            <div className="info-section">
              <h3>{t("subscription.currentPlan", "Current Plan")}</h3>
              <div className="plan-badge plan-badge-active">{planName}</div>
            </div>

            <div className="info-section">
              <h3>{t("subscription.status", "Status")}</h3>
              <div className={`status-badge status-${status || "unknown"}`}>
                {status === "active" && t("subscription.active", "✓ Active")}
                {status === "trialing" &&
                  t("subscription.trialing", "⏳ Trialing")}
                {status === "past_due" &&
                  t("subscription.pastDue", "⚠️ Past Due")}
                {status === "canceled" &&
                  t("subscription.canceled", "✗ Canceled")}
                {!["active", "trialing", "past_due", "canceled"].includes(
                  status
                ) &&
                  status &&
                  String(status)}
              </div>
            </div>

            {currentPeriodEnd && (
              <div className="info-section">
                <h3>
                  {t("subscription.nextBillingDate", "Next Billing Date")}
                </h3>
                <p className="billing-date">{formatDate(currentPeriodEnd)}</p>
              </div>
            )}

            {cancelAtPeriodEnd && currentPeriodEnd && (
              <div className="info-section warning">
                <h3>
                  {t(
                    "subscription.subscriptionEnding",
                    "⚠️ Subscription Ending"
                  )}
                </h3>
                <p>
                  {t(
                    "subscription.subscriptionEndsOn",
                    "Your subscription will end on"
                  )}{" "}
                  {formatDate(currentPeriodEnd)}.
                </p>
              </div>
            )}
          </div>

          <div
            style={{
              padding: "24px",
              border: "1px solid #e0e0e0",
              margin: "20px 0",
              fontFamily: "monospace",
              borderRadius: "10px",
              backgroundColor: "#fafafa",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "15px",
              }}
            >
              <h1 style={{ margin: 0, color: "#1a3a52", fontSize: "18px" }}>
                {t("subscription.statusDetails", "Subscription Status Details")}
              </h1>
              <button
                onClick={() => {
                  trackFeatureUsed({
                    section: "billing",
                    feature: "subscription_details",
                    action: showDetails ? "hide" : "show",
                    source: "button",
                  });
                  setShowDetails(!showDetails);
                }}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#1a3a52",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.backgroundColor = "#2d5f7e")
                }
                onMouseLeave={(e) =>
                  (e.target.style.backgroundColor = "#1a3a52")
                }
              >
                {showDetails
                  ? t("subscription.hideDetails", "▼ Hide Details")
                  : t("subscription.showDetails", "▶ Show Details")}
              </button>
            </div>

            {showDetails && (
              <div style={{ marginTop: "20px", color: "#2c2c2c" }}>
                <h4 style={{ color: "#1a3a52" }}>
                  {t("subscription.status", "Status")}:
                </h4>
                <ul>
                  <li>
                    <strong>
                      {t("subscription.hasSubscription", "Has Subscription:")}
                    </strong>{" "}
                    {subscription
                      ? t("subscription.yes", "Yes")
                      : t("subscription.no", "No")}
                  </li>
                  <li>
                    <strong>{t("subscription.isActive", "Is Active:")}</strong>{" "}
                    {isActive
                      ? t("subscription.yes", "Yes")
                      : t("subscription.no", "No")}
                  </li>
                  <li>
                    <strong>
                      {t("subscription.isRecurring", "Is Recurring:")}
                    </strong>{" "}
                    {isRecurring
                      ? t("subscription.yes", "Yes")
                      : t("subscription.no", "No")}
                  </li>
                  <li>
                    <strong>
                      {t(
                        "subscription.cancelAtPeriodEnd",
                        "Cancel At Period End:"
                      )}
                    </strong>{" "}
                    {cancelAtPeriodEnd
                      ? t("subscription.yes", "Yes")
                      : t("subscription.no", "No")}
                  </li>
                  <li>
                    <strong>{t("subscription.status", "Status")}:</strong>{" "}
                    {status
                      ? String(status)
                      : t("subscription.noSubscription", "No subscription")}
                  </li>
                </ul>

                {subscription && (
                  <>
                    <h4 style={{ color: "#1a3a52" }}>
                      {t(
                        "subscription.subscriptionDetails",
                        "Subscription Details:"
                      )}
                    </h4>
                    <ul>
                      {startDate && (
                        <li>
                          <strong>
                            {t("subscription.startDate", "Start Date:")}
                          </strong>{" "}
                          {formatDate(startDate)}
                        </li>
                      )}
                      {endDate && (
                        <li>
                          <strong>
                            {t("subscription.endDate", "End Date:")}
                          </strong>{" "}
                          {formatDate(endDate)}
                        </li>
                      )}
                      {currentPeriodEnd && (
                        <li>
                          <strong>
                            {t(
                              "subscription.currentPeriodEnd",
                              "Current Period End:"
                            )}
                          </strong>{" "}
                          {formatDate(currentPeriodEnd)}
                        </li>
                      )}
                    </ul>
                  </>
                )}

                {!subscription && (
                  <p style={{ color: "#666666", fontStyle: "italic" }}>
                    {t(
                      "subscription.noActiveSubscription",
                      "No active subscription found. This is normal if the user hasn't subscribed yet."
                    )}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="manage-actions">
            <ManageSubscriptionButton
              buttonText={t("subscription.manageButton", "Manage Subscription")}
              variant="primary"
            />
          </div>
        </div>

        {/* Testing Section - Always visible in development */}
        {process.env.NODE_ENV === "development" && currentUser && (
          <div
            style={{
              marginTop: "40px",
              padding: "20px",
              border: "2px dashed #ff9800",
              borderRadius: "8px",
              backgroundColor: "#fff3e0",
              maxWidth: "800px",
              margin: "40px auto 0",
            }}
          >
            <h3 style={{ marginTop: 0, color: "#e65100" }}>
              {t("subscription.testModeTitle", "🧪 Testing Mode - Simulate Subscriptions")}
            </h3>
            <p
              style={{ color: "#666", fontSize: "14px", marginBottom: "20px" }}
            >
              {t("subscription.testModeDescription", "These buttons simulate subscription purchases for testing. They create the same Firestore structure as Stripe would.")}
            </p>

            {testError && (
              <div
                style={{
                  padding: "10px",
                  backgroundColor: "#ffebee",
                  color: "#c62828",
                  borderRadius: "4px",
                  marginBottom: "15px",
                }}
              >
                {testError}
              </div>
            )}

            {testSuccess && (
              <div
                style={{
                  padding: "10px",
                  backgroundColor: "#e8f5e9",
                  color: "#2e7d32",
                  borderRadius: "4px",
                  marginBottom: "15px",
                }}
              >
                {testSuccess}
              </div>
            )}

            <div style={{ marginBottom: "20px" }}>
              <h4 style={{ marginBottom: "10px" }}>Simulate Subscription:</h4>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: "10px",
                  marginBottom: "15px",
                }}
              >
                <button
                  onClick={() => handleTestSubscription("basic", "monthly")}
                  disabled={testLoading}
                  style={{
                    padding: "10px",
                    backgroundColor: testLoading ? "#ccc" : "#4CAF50",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: testLoading ? "not-allowed" : "pointer",
                    fontWeight: "600",
                  }}
                >
                  Basic Monthly
                </button>
                <button
                  onClick={() => handleTestSubscription("basic", "yearly")}
                  disabled={testLoading}
                  style={{
                    padding: "10px",
                    backgroundColor: testLoading ? "#ccc" : "#4CAF50",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: testLoading ? "not-allowed" : "pointer",
                    fontWeight: "600",
                  }}
                >
                  Basic Yearly
                </button>
                <button
                  onClick={() => handleTestSubscription("pro", "monthly")}
                  disabled={testLoading}
                  style={{
                    padding: "10px",
                    backgroundColor: testLoading ? "#ccc" : "#2196F3",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: testLoading ? "not-allowed" : "pointer",
                    fontWeight: "600",
                  }}
                >
                  Pro Monthly
                </button>
                <button
                  onClick={() => handleTestSubscription("pro", "yearly")}
                  disabled={testLoading}
                  style={{
                    padding: "10px",
                    backgroundColor: testLoading ? "#ccc" : "#2196F3",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: testLoading ? "not-allowed" : "pointer",
                    fontWeight: "600",
                  }}
                >
                  Pro Yearly
                </button>
                <button
                  onClick={() => handleTestSubscription("elite", "monthly")}
                  disabled={testLoading}
                  style={{
                    padding: "10px",
                    backgroundColor: testLoading ? "#ccc" : "#9C27B0",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: testLoading ? "not-allowed" : "pointer",
                    fontWeight: "600",
                  }}
                >
                  Elite Monthly
                </button>
                <button
                  onClick={() => handleTestSubscription("elite", "yearly")}
                  disabled={testLoading}
                  style={{
                    padding: "10px",
                    backgroundColor: testLoading ? "#ccc" : "#9C27B0",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: testLoading ? "not-allowed" : "pointer",
                    fontWeight: "600",
                  }}
                >
                  Elite Yearly
                </button>
              </div>
            </div>

            <div>
              <button
                onClick={handleCancelTestSubscription}
                disabled={testLoading}
                style={{
                  padding: "12px 24px",
                  backgroundColor: testLoading ? "#ccc" : "#f44336",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: testLoading ? "not-allowed" : "pointer",
                  fontWeight: "600",
                  fontSize: "14px",
                }}
              >
                {testLoading ? "Processing..." : "Cancel Test Subscription"}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Show subscription plans if user is not subscribed
  return (
    <div className="manage-subscription-container">
      {/* Error Modal */}
      <Modal
        isOpen={!!error}
        onClose={() => setError(null)}
        type="error"
        message={error}
      />

      {/* Hero Header */}
      <div className="subscription-hero">
        <h1>{t("subscription.choosePlan", "Choose Your Plan")}</h1>
        <p>
          {t(
            "subscription.plansDescription",
            "Select a subscription plan to get started with premium features."
          )}
        </p>
      </div>

      <div className="subscription-content">
        {/* Billing Period Toggle */}
        <div className="billing-toggle-container">
          <button
            className={`billing-toggle-btn ${
              selectedBillingPeriod === "yearly" ? "active" : ""
            }`}
            onClick={() => setSelectedBillingPeriod("yearly")}
          >
            {t("subscription.yearly", "Yearly")}
            <span className="billing-savings">
              {t("subscription.savePercent", "Save 20%")}
            </span>
          </button>
          <button
            className={`billing-toggle-btn ${
              selectedBillingPeriod === "monthly" ? "active" : ""
            }`}
            onClick={() => setSelectedBillingPeriod("monthly")}
          >
            {t("subscription.monthly", "Monthly")}
          </button>
        </div>

        <div className="plans-grid">
          {(() => {
            const allTiers = tierService.getAllTiers();
            const tierHierarchy = tierService.getTierHierarchy();
            // Only show free, basic, pro in the main grid
            const mainTiers = tierHierarchy.filter((id) => id !== "elite");

            return (
              <>
                {mainTiers.map((tierId) => {
                  const tier = allTiers[tierId];
                  if (!tier) return null;

                  const priceIds = tierService.getPriceIdsForTier(tierId);
                  const price = tierService.getTierPrice(
                    tier,
                    selectedBillingPeriod
                  );
                  const priceId = priceIds?.[selectedBillingPeriod];
                  const isCurrentTier = tierId === userTierId;
                  const isPro = tierId === "pro";

                  // Build features list
                  const features = [];
                  if (tierId === "free") {
                    features.push(
                      t("subscription.freeFeature1", "Education (basic)")
                    );
                    features.push(
                      t("subscription.freeFeature2", "Browse articles")
                    );
                  } else if (tierId === "basic") {
                    features.push(
                      t("subscription.basicFeature1", "Everything in Free")
                    );
                    features.push(
                      t(
                        "subscription.basicFeature2",
                        "Personal credit articles"
                      )
                    );
                    features.push(
                      t("subscription.basicFeature3", "1 AI plan /month")
                    );
                    features.push(
                      t("subscription.basicFeature4", "Card inventory")
                    );
                  } else if (tierId === "pro") {
                    features.push(
                      t("subscription.proFeature1", "Everything in Basic")
                    );
                    features.push(
                      t("subscription.proFeature2", "Business credit articles")
                    );
                    features.push(
                      t("subscription.proFeature3", "10 AI plans/mo")
                    );
                    features.push(
                      t("subscription.proFeature4", "Full card inventory")
                    );
                    features.push(
                      t("subscription.proFeature5", "Report parser")
                    );
                    features.push(
                      t("subscription.proFeature6", "Both personal + business")
                    );
                  }

                  // Get tagline
                  const taglines = {
                    free: t("subscription.freeTagline", "Get started"),
                    basic: t(
                      "subscription.basicTagline",
                      "Personal credit builder"
                    ),
                    pro: t("subscription.proTagline", "Complete solution"),
                  };

                  // Get button text
                  const buttonTexts = {
                    free: t("subscription.tryFree", "Try Free"),
                    basic: t("subscription.startBasic", "Start Basic"),
                    pro: t("subscription.startPro", "Start Pro"),
                  };

                  return (
                    <div
                      key={tierId}
                      className={`plan-card ${
                        isPro ? "plan-card-featured" : ""
                      }`}
                    >
                      {isPro && (
                        <div className="plan-badge-featured">
                          {t("subscription.popular", "POPULAR")}
                        </div>
                      )}
                      <div className="plan-header">
                        <h3>{tierService.getTierName(tier)}</h3>
                        <div className="plan-price">
                          <span className="price-amount">${price}</span>
                          <span className="price-period">
                            {tierId === "free"
                              ? t("subscription.forever", "/forever")
                              : selectedBillingPeriod === "monthly"
                              ? t("subscription.perMonth", "/month")
                              : t("subscription.perYear", "/year")}
                          </span>
                        </div>
                        <p className="plan-tagline">{taglines[tierId]}</p>
                      </div>
                      <ul className="plan-features">
                        {features.map((feature, idx) => (
                          <li key={idx}>{feature}</li>
                        ))}
                      </ul>
                      {isCurrentTier ? (
                        <button className="plan-button" disabled>
                          {t("subscription.currentPlan", "Current Plan")}
                        </button>
                      ) : (
                        <button
                          className={`plan-button ${
                            isPro ? "plan-button-primary" : ""
                          }`}
                          onClick={() =>
                            tierId === "free"
                              ? null
                              : priceId &&
                                handleSubscribe(
                                  priceId,
                                  tierId,
                                  selectedBillingPeriod
                                )
                          }
                          disabled={tierId !== "free" && (loading || !priceId)}
                        >
                          {loading && tierId !== "free"
                            ? t("subscription.processing", "Processing...")
                            : buttonTexts[tierId] ||
                              t("subscription.subscribe", "Subscribe")}
                        </button>
                      )}
                    </div>
                  );
                })}
              </>
            );
          })()}
        </div>

        {/* Elite Banner */}
        {(() => {
          const allTiers = tierService.getAllTiers();
          const eliteTier = allTiers["elite"];
          if (!eliteTier) return null;

          const priceIds = tierService.getPriceIdsForTier("elite");
          const price = tierService.getTierPrice(
            eliteTier,
            selectedBillingPeriod
          );
          const priceId = priceIds?.[selectedBillingPeriod];
          const isCurrentTier = userTierId === "elite";

          const eliteFeatures = [
            t("subscription.eliteFeature1", "Everything in Pro"),
            t("subscription.eliteFeature2", "AI Copilot assistance"),
            t("subscription.eliteFeature3", "Unlimited report parses"),
            t("subscription.eliteFeature4", "20 AI plans/month"),
            t("subscription.eliteFeature5", "Client management"),
            t("subscription.eliteFeature6", "Priority support"),
          ];

          return (
            <div className="elite-banner">
              <div className="elite-info">
                <h3>ELITE</h3>
                <p className="elite-price">
                  ${price}/
                  {selectedBillingPeriod === "yearly"
                    ? t("subscription.year", "year")
                    : t("subscription.month", "month")}
                </p>
                <p className="elite-tagline">
                  {t("subscription.eliteTagline", "For mentors & advisors")}
                </p>
              </div>
              <div className="elite-features">
                {eliteFeatures.map((feature, idx) => (
                  <span key={idx}>{feature}</span>
                ))}
              </div>
              {isCurrentTier ? (
                <button className="elite-button" disabled>
                  {t("subscription.currentPlan", "Current Plan")}
                </button>
              ) : (
                <button
                  className="elite-button"
                  onClick={() =>
                    priceId &&
                    handleSubscribe(priceId, "elite", selectedBillingPeriod)
                  }
                  disabled={loading || !priceId}
                >
                  {loading
                    ? t("subscription.processing", "Processing...")
                    : t("subscription.startElite", "Start Elite")}
                </button>
              )}
            </div>
          );
        })()}

        <p className="plans-note">
          {t(
            "subscription.moneyBackGuarantee",
            "All plans include a 7-day money-back guarantee. Cancel anytime."
          )}
        </p>

        {/* Testing Section - Only visible in development */}
        {process.env.NODE_ENV === "development" && currentUser && (
          <div
            style={{
              marginTop: "40px",
              padding: "20px",
              border: "2px dashed #ff9800",
              borderRadius: "8px",
              backgroundColor: "#fff3e0",
            }}
          >
            <h3 style={{ marginTop: 0, color: "#e65100" }}>
              {t("subscription.testModeTitle", "🧪 Testing Mode - Simulate Subscriptions")}
            </h3>
            <p
              style={{ color: "#666", fontSize: "14px", marginBottom: "20px" }}
            >
              {t("subscription.testModeDescription", "These buttons simulate subscription purchases for testing. They create the same Firestore structure as Stripe would.")}
            </p>

            {testError && (
              <div
                style={{
                  padding: "10px",
                  backgroundColor: "#ffebee",
                  color: "#c62828",
                  borderRadius: "4px",
                  marginBottom: "15px",
                }}
              >
                {testError}
              </div>
            )}

            {testSuccess && (
              <div
                style={{
                  padding: "10px",
                  backgroundColor: "#e8f5e9",
                  color: "#2e7d32",
                  borderRadius: "4px",
                  marginBottom: "15px",
                }}
              >
                {testSuccess}
              </div>
            )}

            <div style={{ marginBottom: "20px" }}>
              <h4 style={{ marginBottom: "10px" }}>{t("subscription.simulateSubscription", "Simulate Subscription:")}</h4>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: "10px",
                  marginBottom: "15px",
                }}
              >
                <button
                  onClick={() => handleTestSubscription("basic", "monthly")}
                  disabled={testLoading}
                  style={{
                    padding: "10px",
                    backgroundColor: testLoading ? "#ccc" : "#4CAF50",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: testLoading ? "not-allowed" : "pointer",
                    fontWeight: "600",
                  }}
                >
                  {t("subscription.basicMonthly", "Basic Monthly")}
                </button>
                <button
                  onClick={() => handleTestSubscription("basic", "yearly")}
                  disabled={testLoading}
                  style={{
                    padding: "10px",
                    backgroundColor: testLoading ? "#ccc" : "#4CAF50",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: testLoading ? "not-allowed" : "pointer",
                    fontWeight: "600",
                  }}
                >
                  {t("subscription.basicYearly", "Basic Yearly")}
                </button>
                <button
                  onClick={() => handleTestSubscription("pro", "monthly")}
                  disabled={testLoading}
                  style={{
                    padding: "10px",
                    backgroundColor: testLoading ? "#ccc" : "#2196F3",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: testLoading ? "not-allowed" : "pointer",
                    fontWeight: "600",
                  }}
                >
                  {t("subscription.proMonthly", "Pro Monthly")}
                </button>
                <button
                  onClick={() => handleTestSubscription("pro", "yearly")}
                  disabled={testLoading}
                  style={{
                    padding: "10px",
                    backgroundColor: testLoading ? "#ccc" : "#2196F3",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: testLoading ? "not-allowed" : "pointer",
                    fontWeight: "600",
                  }}
                >
                  {t("subscription.proYearly", "Pro Yearly")}
                </button>
                <button
                  onClick={() => handleTestSubscription("elite", "monthly")}
                  disabled={testLoading}
                  style={{
                    padding: "10px",
                    backgroundColor: testLoading ? "#ccc" : "#9C27B0",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: testLoading ? "not-allowed" : "pointer",
                    fontWeight: "600",
                  }}
                >
                  {t("subscription.eliteMonthly", "Elite Monthly")}
                </button>
                <button
                  onClick={() => handleTestSubscription("elite", "yearly")}
                  disabled={testLoading}
                  style={{
                    padding: "10px",
                    backgroundColor: testLoading ? "#ccc" : "#9C27B0",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: testLoading ? "not-allowed" : "pointer",
                    fontWeight: "600",
                  }}
                >
                  {t("subscription.eliteYearly", "Elite Yearly")}
                </button>
              </div>
            </div>

            <div>
              <button
                onClick={handleCancelTestSubscription}
                disabled={testLoading}
                style={{
                  padding: "12px 24px",
                  backgroundColor: testLoading ? "#ccc" : "#f44336",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: testLoading ? "not-allowed" : "pointer",
                  fontWeight: "600",
                  fontSize: "14px",
                }}
              >
                {testLoading ? t("subscription.processing", "Processing...") : t("subscription.cancelTestSubscription", "Cancel Test Subscription")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageSubscription;
